import MiscellaneousHome from "../../../../common/icons/MiscellaneousHome.tsx";
import MiscellaneousBook from "../../../../common/icons/MiscellaneousBook.tsx";
import MiscellaneousSearch from "../../../../common/icons/MiscellaneousSearch.tsx";
import BookshelfExplore from "../../../../common/icons/BookshelfExplore.tsx";
import SubscriptionPopover from "./SubscriptionPopover.tsx";
import SettingsGear from "../../../../common/icons/SettingsGear.tsx";
import { useIntl } from "react-intl";
import { useHomeContextUser } from "../../homeContext.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { ReactElement, ReactNode, useMemo } from "react";
import MiscellaneousRecommendation from "../../../../common/icons/MiscellaneousRecommendation.tsx";
import useSupportsRecommendations from "../../common/recommendations/useSupportsRecommendations.ts";
import useUserPreference from "../../actions/user/useUserPreference.ts";
import { UserPreferenceKey } from "../../../../common/api/decimal/methods/user.ts";
import NewTag from "./NewTag.tsx";
import useIsSampleSearchNew from "routes/home/common/sample-search/useIsSampleSearchNew.ts";
import EditFolder from "../../../../common/icons/EditFolder.tsx";
import useSupportsAdoptions from "../../../../common/features/adoptions/useSupportsAdoptions.ts";

export interface LinkData {
  label: string;
  accessibleLabel?: string;
  path: string;
  icon?: ReactNode;
  enabled: boolean;
  popoverContent?: ReactElement;
  annotation?: ReactNode;
  iconAnnotation?: ReactNode;
}

function isRecommendationsNew() {
  const recommendationsNewDays = 30;
  const recommendationsNewEndDate = new Date(2025, 0, 6); // initialize to release date
  recommendationsNewEndDate.setDate(
    recommendationsNewEndDate.getDate() + recommendationsNewDays
  );
  return new Date() < recommendationsNewEndDate;
}

export default function useSidebarLinks() {
  const intl = useIntl();
  const user = useHomeContextUser();
  const brand = useBrandLoaderData();
  const supportsRecommendations = useSupportsRecommendations();
  const [recommendationsSeenDate] = useUserPreference(
    UserPreferenceKey.LastSeenRecommendations
  );
  const isSampleSearchNew = useIsSampleSearchNew();
  const [sampleSearchSeenDate] = useUserPreference(
    UserPreferenceKey.LastSeenSampleSearch
  );
  const supportsAdoptions = useSupportsAdoptions();

  const unseenExpiredLabel = intl.formatMessage(
    {
      defaultMessage:
        "{count, plural, =1 {1 title has} other {# titles have}} recently expired",
    },
    { count: user.unseenExpiredCount }
  );

  const unseenRecommendationsLabel = intl.formatMessage(
    {
      defaultMessage:
        "{count, plural, =1 {1 new recommendation} other {# new recommendations}}",
    },
    { count: user.unseenRecommendationsCount }
  );

  const permaLinks: LinkData[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Home" }),
      accessibleLabel: intl.formatMessage({ defaultMessage: "Home Dashboard" }),
      path: "/dashboard",
      icon: <MiscellaneousHome />,
      enabled: true,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Search" }),
      path: "/search",
      icon: <MiscellaneousSearch />,
      enabled: brand.Search.enabled,
      iconAnnotation:
        isSampleSearchNew && !sampleSearchSeenDate ? <NewTag /> : null,
    },
    {
      label: intl.formatMessage({ defaultMessage: "My Courses" }),
      path: "/my-courses",
      icon: <MiscellaneousBook />,
      enabled: brand.MyCourses.enabled,
    },
    {
      label: "Adoptions Prototype",
      path: "/adoptions",
      icon: <EditFolder />,
      enabled: supportsAdoptions,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Recommendations" }),
      path: "/recommendations",
      icon: <MiscellaneousRecommendation />,
      enabled: supportsRecommendations,
      iconAnnotation:
        isRecommendationsNew() && !recommendationsSeenDate ? <NewTag /> : null,
      annotation: user.unseenRecommendationsCount > 0 && (
        <span className={"vst-badge"} aria-label={unseenRecommendationsLabel}>
          {user.unseenRecommendationsCount}
        </span>
      ),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Explore" }),
      path: "/explore",
      icon: <BookshelfExplore />,
      enabled: brand.Explore.enabled && user.hasExplore,
      popoverContent: <SubscriptionPopover />,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Admin" }),
      path: "/admin",
      icon: <SettingsGear />,
      enabled: user.hasAdmin && brand.Rostering.enabled,
    },
  ].filter((el) => el.enabled);

  const shelfLinks: LinkData[] = [
    {
      label: intl.formatMessage({ defaultMessage: "My Library" }),
      path: "/my-library",
      enabled: true,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Groups" }),
      path: "/classes",
      enabled: user.hasGroups && brand.Rostering.enabled,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Favorites" }),
      path: "/favorites",
      enabled: true,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Expired" }),
      path: "/expired",
      enabled: brand.Expired.enabled && user.hasExpiredLicenses,
      annotation: user.unseenExpiredCount > 0 && (
        <span className={"vst-badge"} aria-label={unseenExpiredLabel}>
          {user.unseenExpiredCount}
        </span>
      ),
    },
  ].filter((el) => el.enabled);

  return useMemo(
    () => ({
      permaLinks,
      shelfLinks,
    }),
    [permaLinks, shelfLinks]
  );
}
